import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { FiMenu, FiX, FiUser, FiLogOut, FiHome, FiBookOpen, FiCalendar, FiSettings } from 'react-icons/fi';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [settings, setSettings] = useState(null);
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowDropdown(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              {settings?.logoUrl ? (
                <img src={settings.logoUrl} alt="Logo" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">{settings?.siteName?.[0] || 'M'}</span>
                </div>
              )}
              <span className="text-2xl font-bold gradient-text hidden sm:block">{settings?.siteName || 'MathTutoring'}</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-primary-500 transition flex items-center space-x-1">
              <FiHome />
              <span>Home</span>
            </Link>
            <Link to="/courses" className="text-gray-700 hover:text-primary-500 transition flex items-center space-x-1">
              <FiBookOpen />
              <span>Courses</span>
            </Link>
            {isAuthenticated && (
              <Link to="/booking" className="text-gray-700 hover:text-primary-500 transition flex items-center space-x-1">
                <FiCalendar />
                <span>Book Now</span>
              </Link>
            )}
            
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary-500 transition"
                >
                  <div className="w-9 h-9 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <span className="font-medium">{user?.firstName}</span>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    <Link
                      to={isAdmin ? "/admin/dashboard" : "/dashboard"}
                      className="block px-4 py-2 text-gray-700 hover:bg-primary-50 hover:text-primary-500 transition flex items-center space-x-2"
                      onClick={() => setShowDropdown(false)}
                    >
                      {isAdmin ? <FiSettings /> : <FiUser />}
                      <span>{isAdmin ? 'Admin Panel' : 'Dashboard'}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-500 transition flex items-center space-x-2"
                    >
                      <FiLogOut />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-gray-700 hover:text-primary-500 transition font-medium">
                  Login
                </Link>
                <Link to="/register" className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2 rounded-lg hover:shadow-lg transition transform hover:-translate-y-0.5">
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-primary-500 transition"
            >
              {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/"
              className="block px-3 py-2 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-md transition"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/courses"
              className="block px-3 py-2 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-md transition"
              onClick={() => setIsOpen(false)}
            >
              Courses
            </Link>
            {isAuthenticated && (
              <>
                <Link
                  to="/booking"
                  className="block px-3 py-2 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-md transition"
                  onClick={() => setIsOpen(false)}
                >
                  Book Now
                </Link>
                <Link
                  to={isAdmin ? "/admin/dashboard" : "/dashboard"}
                  className="block px-3 py-2 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-md transition"
                  onClick={() => setIsOpen(false)}
                >
                  {isAdmin ? 'Admin Panel' : 'Dashboard'}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition"
                >
                  Logout
                </button>
              </>
            )}
            {!isAuthenticated && (
              <>
                <Link
                  to="/login"
                  className="block px-3 py-2 text-gray-700 hover:bg-primary-50 hover:text-primary-500 rounded-md transition"
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block px-3 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-md hover:shadow-lg transition"
                  onClick={() => setIsOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;

